// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import isEqual from 'lodash-es/isEqual'
import {useCallback} from 'react'

import type {DraftIssue} from '../content-preview-types'
import {useContentPreview} from '../ContentPreviewContext'
import {useUserEditedNewIssueId} from './use-user-edited-new-issue-id'

export function useOnChangeCallback(draftIssue: DraftIssue) {
  const {openItem, updateItem} = useContentPreview()
  const {selectedThreadID} = useChatState()
  const userEditedNewIssueId = useUserEditedNewIssueId(draftIssue)
  const storageKey = `edited-${selectedThreadID}-${userEditedNewIssueId}`
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, writeEditedIssueToStorage] = useSessionStorage<DraftIssue | undefined>(storageKey, undefined)

  return useCallback(
    (editedIssue: Partial<DraftIssue>, skipVersionBump: boolean = false) => {
      let hasChanges = false
      let property: keyof typeof editedIssue
      for (property in editedIssue) {
        if (!isEqual(editedIssue[property], draftIssue[property])) {
          hasChanges = true

          // For metadata fields only, report changes
          if (property !== 'name' && property !== 'body' && property !== 'defaultValuesById') {
            sendEvent('dotcom_chat.activate', {
              target: 'BROWSER_ISSUE_USER_CHANGED_FIELD',
              mode: 'immersive',
              threadId: selectedThreadID,
              repository: draftIssue.repository,
              issueTag: draftIssue.tag,
              userChangedField: property,
              wasEmpty: wasEmpty(draftIssue[property]),
            })
          }
        }
      }

      if (!hasChanges) {
        return
      }

      const updatedItem: DraftIssue = {
        ...draftIssue,
        ...editedIssue,
      }

      if (!skipVersionBump) {
        updatedItem.id = userEditedNewIssueId
        updatedItem.isUserEdited = true
        writeEditedIssueToStorage(updatedItem)
      }

      updateItem(updatedItem)
      openItem(updatedItem.id)
    },
    [draftIssue, updateItem, openItem, selectedThreadID, userEditedNewIssueId, writeEditedIssueToStorage],
  )
}

export function wasEmpty(value: unknown): boolean {
  switch (typeof value) {
    case 'string':
      return !value
    case 'object':
      if (Array.isArray(value)) {
        return !value || (value as unknown[]).length === 0
      } else {
        return value == null
      }
    default:
      return value == null
  }
}
